import { cn } from "@msuite/ui/utils";
import type { Enums } from "@msuite/utils/types";
import type { PropsWithChildren, ReactNode } from "react";

/** Props Interface */
interface CapacityPlannerRowHeadProps extends PropsWithChildren {
	handle?: ReactNode;
	actions?: ReactNode;
	/**
	 * Marks the row's left edge in its own colour — the same one its bar is drawn in, so the name
	 * and the days across from it read as one row on a board deep enough to lose track.
	 */
	accentColor?: Enums<"color">;
}

/**
 * The first column of every planner row: a fixed lane for the expand handle, the label, and the
 * row's actions at the right edge.
 *
 * A crew's rows and the manager windows under it use the same three columns, and a sub row simply
 * leaves the handle lane empty — which is what keeps every name on the board starting at the same
 * x, however deep its row sits.
 */
export function CapacityPlannerRowHead({
	handle,
	children,
	actions,
	accentColor,
}: CapacityPlannerRowHeadProps) {
	/** Render */
	return (
		<div
			className={cn(
				"items-center gap-2 grid grid-cols-[1.75rem_minmax(0,1fr)_max-content] min-w-0",
				accentColor && "border-l-2",
			)}
			style={accentColor ? { borderLeftColor: `var(--color-${accentColor}-500)` } : undefined}
		>
			<div className="flex-center">{handle}</div>
			<div className="v-stack gap-0 min-w-0">{children}</div>
			<div className="h-stack gap-1 shrink-0">{actions}</div>
		</div>
	);
}
